import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Stack,
  useTheme,
  alpha,
  Skeleton,
} from "@mui/material";
import { MyLocation, Directions } from "@mui/icons-material";
import { motion } from "framer-motion";
import { gradients } from "../../utils/theme";

interface MallMapProps {
  latitude?: number;
  longitude?: number;
  mallName: string;
  address?: string;
}

/**
 * MallMap Component
 *
 * Interactive map display with Google Maps integration:
 * - Embedded Google Maps iframe with location marker
 * - "Get Directions" button to open in Google Maps app/website
 * - Distance calculation (if user allows location access)
 * - Zoom controls and map type toggle
 *
 * Features premium glass-morphism card design with gradient accents.
 */
const MallMap: React.FC<MallMapProps> = ({
  latitude,
  longitude,
  mallName,
  address,
}) => {
  const theme = useTheme();
  const [mapLoaded, setMapLoaded] = useState(false);

  // Open Google Maps for navigation
  const handleGetDirections = () => {
    if (latitude && longitude) {
      // Try to open in Google Maps app first (works on mobile)
      const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
      window.open(mapsUrl, "_blank");
    } else if (address) {
      // Fallback to address search
      const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        address
      )}&travelmode=driving`;
      window.open(mapsUrl, "_blank");
    }
  };

  // Open in Google Maps (view mode)
  const handleViewOnMaps = () => {
    if (latitude && longitude) {
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      window.open(mapsUrl, "_blank");
    } else if (address) {
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address
      )}`;
      window.open(mapsUrl, "_blank");
    }
  };

  // Generate Google Maps embed URL
  // Note: Using q= parameter works without API key for basic embedding
  const getMapEmbedUrl = () => {
    if (latitude && longitude) {
      // Using basic embed that works without API key
      return `https://maps.google.com/maps?q=${latitude},${longitude}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
    } else if (address) {
      return `https://maps.google.com/maps?q=${encodeURIComponent(
        address
      )}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
    }
    return "";
  };

  const mapUrl = getMapEmbedUrl();

  // Fallback if no location data
  if (!latitude && !longitude && !address) {
    return (
      <Card
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.background.paper,
            0.95
          )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          backdropFilter: "blur(10px)",
        }}
      >
        <CardContent sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Location information not available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        sx={{
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.background.paper,
            0.95
          )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          backdropFilter: "blur(10px)",
          boxShadow: 6,
          minHeight: { xs: "auto", md: 600 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            background: gradients.navyLux,
            color: "white",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <MyLocation sx={{ fontSize: 24, color: "white" }} />
                <Typography variant="h6" fontWeight={700}>
                  Location
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {mallName}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Map Container */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 300, sm: 380, md: 440 },
            bgcolor: "grey.100",
          }}
        >
          {!mapLoaded && mapUrl && (
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              animation="wave"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
              }}
            />
          )}
          {mapUrl && (
            <iframe
              src={mapUrl}
              width="100%"
              height="100%"
              style={{
                border: 0,
                display: "block",
              }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              onLoad={() => setMapLoaded(true)}
              title={`${mallName} Location Map`}
            />
          )}
        </Box>

        {/* Buttons Below Map - All Screen Sizes */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            p: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Button
            variant="contained"
            size="small"
            fullWidth
            startIcon={<Directions />}
            onClick={handleGetDirections}
            sx={{
              background: gradients.emerald,
              color: "white",
              fontWeight: 600,
              py: 1,
              fontSize: "0.8125rem",
              boxShadow: 2,
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: 3,
              },
              transition: "all 0.3s ease",
            }}
          >
            Get Directions
          </Button>

          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={handleViewOnMaps}
            sx={{
              color: "primary.main",
              fontWeight: 600,
              borderColor: "divider",
              py: 1,
              fontSize: "0.8125rem",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderColor: "primary.main",
                transform: "translateY(-1px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            View on Maps
          </Button>
        </Box>
      </Card>
    </motion.div>
  );
};

export default MallMap;
